/**
 * The tally strata (PRD-333, the article-done frame): a flat Card, a Tally (a big count over its
 * caption) and a TallyBar (a label, a bar filled in proportion to a total, the count). Every paint is a
 * palette hex handed in; the bar's width is a share of the total, never a typed pixel, and a nonzero
 * count always shows at least its own dot.
 */
import type { ReactNode } from 'react';
import { ARTICLE, CONTROL, RADIUS, SPACE, TEXT, WEIGHT, usePalette, type Hex } from '../theme';

/** The centred column a done screen sits in. */
export function Sheet({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: SPACE.xl,
        maxWidth: ARTICLE.sheet,
        margin: '0 auto',
        padding: `${SPACE.xxl}px 0`,
        boxSizing: 'border-box',
      }}
    >
      {children}
    </div>
  );
}

/** A flat card: the ground's own surface on a hairline. `grow` shares a row equally with its siblings. */
export function Card({
  children,
  label,
  grow = false,
  row = false,
}: {
  children: ReactNode;
  label?: string;
  grow?: boolean;
  /** Lay the children side by side instead of stacked. */
  row?: boolean;
}) {
  const p = usePalette();
  return (
    <section
      aria-label={label}
      style={{
        display: 'flex',
        flexDirection: row ? 'row' : 'column',
        alignItems: row ? 'center' : undefined,
        gap: row ? SPACE.lg : SPACE.cozy,
        flex: grow ? '1 1 0' : undefined,
        minWidth: 0,
        padding: `${ARTICLE.cardPadY}px ${ARTICLE.cardPadX}px`,
        background: p.surface,
        border: `1px solid ${p.hairline}`,
        borderRadius: RADIUS.card,
        boxSizing: 'border-box',
      }}
    >
      {children}
    </section>
  );
}

/** A count in large type over its caption; the paint colours the count. */
export function Tally({ value, caption, paint }: { value: number; caption: ReactNode; paint?: Hex }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minWidth: 0 }}>
      <span
        style={{
          fontSize: ARTICLE.tally,
          fontWeight: WEIGHT.strong,
          fontVariantNumeric: 'tabular-nums',
          color: paint ?? p.inkStrong,
          lineHeight: 'normal',
        }}
      >
        {value}
      </span>
      <span style={{ fontSize: TEXT.caption, color: p.inkSecondary, lineHeight: 'normal' }}>{caption}</span>
    </div>
  );
}

/** The share of a total a count fills, 0 to 1; an empty total fills nothing. */
export function shareOf(value: number, total: number): number {
  return total > 0 ? Math.min(1, value / total) : 0;
}

/** One bar: its label, a track filled to `value` of `total` in the paint, and the count. */
export function TallyBar({ label, value, total, paint }: { label: string; value: number; total: number; paint: Hex }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.snug }}>
      <span style={{ width: ARTICLE.barLabel, flexShrink: 0, fontSize: TEXT.control, color: p.inkSecondary }}>{label}</span>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={value}
        style={{
          flex: '1 1 0',
          minWidth: 0,
          height: ARTICLE.barHeight,
          borderRadius: RADIUS.sm,
          background: p.surfaceRaised,
          overflow: 'hidden',
        }}
      >
        <div
          data-share={shareOf(value, total)}
          style={{
            width: `${shareOf(value, total) * 100}%`,
            minWidth: value > 0 ? CONTROL.dot : 0,
            height: ARTICLE.barHeight,
            borderRadius: RADIUS.sm,
            background: paint,
          }}
        />
      </div>
      <span
        style={{
          width: ARTICLE.barCount,
          flexShrink: 0,
          textAlign: 'right',
          fontSize: TEXT.control,
          color: p.ink,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}
